import { Button, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import React from 'react';
import { useEntityData, useRouteToTab } from '../../../../EntityContext';
import { SidebarHeader } from '../SidebarHeader';
import DescriptionSection from './DescriptionSection';
import LinksSection from './LinksSection';
import SourceRefSection from './SourceRefSection';
import EmptyContentSection from './EmptyContentSection';
import { useTranslation } from '../../../../../../../i18n/useTranslation';
import { sanitizeDescription } from '../../../../../../shared/utils/sanitizationUtils';

interface Properties {
    hideLinksButton?: boolean;
}

interface Props {
    properties?: Properties;
    readOnly?: boolean;
}

export const SidebarAboutSection = ({ properties, readOnly }: Props) => {
    const hideLinksButton = properties?.hideLinksButton;
    const { entityData } = useEntityData();
    const routeToTab = useRouteToTab();
    const { t } = useTranslation();
    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = readOnly || entityData?.readOnly?.readOnly === true;

    const originalDescription = entityData?.properties?.description;
    const editedDescription = entityData?.editableProperties?.description;
    const description = (editedDescription || originalDescription || '').replace(/&nbsp;/g, '');
    const links = entityData?.institutionalMemory?.elements || [];

    // Check if the string is empty
    const isDescEmpty = description.trim() === '';
    const hasContent = !!description || links.length > 0;

    return (
        <div className="pa-4">
            <SidebarHeader
                title={t('About')}
                actions={
                    hasContent && (
                        <Tooltip title={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined}>
                            <Button
                                type="default"
                                onClick={() =>
                                    routeToTab({ tabName: t('Documentation'), tabParams: { editing: true } })
                                }
                                className="icon-button"
                                disabled={isReadOnly}
                            >
                                <EditOutlined />
                            </Button>
                        </Tooltip>
                    )
                }
            />
            {description && <DescriptionSection description={sanitizeDescription(description)} />}
            {(!hasContent || isDescEmpty) && (
                <EmptyContentSection
                    hasLinks={!(links.length > 0)}
                    hideLinksButton={hideLinksButton}
                    readOnly={isReadOnly}
                />
            )}
            {hasContent && <LinksSection hideLinksButton={hideLinksButton} readOnly={isReadOnly} />}
            <SourceRefSection />
        </div>
    );
};

export default SidebarAboutSection;
